import { useEffect, useState } from 'react';
import { api, type AnalyticsSummary, type TokenDay, type TokenWindow } from '../api.ts';

/** The ranges the chart offers, in the order they are read. */
const RANGES: TokenWindow[] = ['7d', '30d', '90d'];

/**
 * Sessions, counted — what this workspace has launched and what it cost.
 *
 * "Session" throughout the copy and `run` throughout the code, which is a
 * deliberate mismatch rather than an oversight: people launch a session, the
 * schema stores a run, and translating once here is cheaper than teaching the
 * store a second name for one table.
 *
 * The page is a snapshot above a range below, and the two are scoped
 * differently on purpose. The tiles are a rolling day — "done today" only means
 * anything against a fixed day — while the chart is whichever range the reader
 * picked. Tying them to one control would have made choosing ninety days
 * silently change what "today" meant.
 */
export function AnalyticsView() {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [series, setSeries] = useState<TokenDay[] | null>(null);
  const [range, setRange] = useState<TokenWindow>('7d');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;

    api
      .analytics()
      .then((result) => current && setSummary(result))
      .catch((caught) => current && setError((caught as Error).message));

    return () => {
      current = false;
    };
  }, []);

  /*
   * The series reloads on its own when the range changes, and the tiles above
   * it do not flicker while it does. Refetching both would blank six numbers
   * that did not change to redraw one line.
   */
  useEffect(() => {
    let current = true;

    api
      .analyticsTokens(range)
      .then((result) => current && setSeries(result.series))
      .catch((caught) => current && setError((caught as Error).message));

    return () => {
      current = false;
    };
  }, [range]);

  /*
   * Both token tiles are summed from the same series the chart plots, rather
   * than fetched as their own figures. A tile and the line beside it that
   * disagree by one day's traffic is the fastest way to make a reader distrust
   * every other number on the page.
   */
  const today = series?.at(-1);
  const week = series?.slice(-7);
  const allTime = summary && summary.tokens.allTime.inputTokens + summary.tokens.allTime.outputTokens;

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Analytics</span>
        </span>
      </header>

      <div className="studio-column wide">
        {error && <div className="banner danger">{error}</div>}

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Sessions</h2>
            <span className="chip">rolling 24 hours</span>
          </div>
          <div className="studio-card-body">
            <div className="tiles">
              <Tile
                label="Total spend"
                value={summary ? `$${(summary.spendCents / 100).toFixed(2)}` : '—'}
                note="across sessions launched in the window"
              />
              <Tile
                label="Active"
                value={summary ? String(summary.sessions.active) : '—'}
                note="running, paused or waiting on a person"
              />
              <Tile
                label="Ready"
                value={summary ? String(summary.sessions.ready) : '—'}
                note="waiting for an approval, not for the machine"
              />
              <Tile
                label="In progress"
                value={summary ? String(summary.sessions.inProgress) : '—'}
                note="running right now; paused is not counted here"
              />
              <Tile
                label="Done today"
                value={summary ? String(summary.sessions.doneToday) : '—'}
                note="completed since midnight UTC"
              />
              <Tile
                label="Cancelled today"
                value={summary ? String(summary.sessions.cancelledToday) : '—'}
                note="stopped by somebody, not by an error"
                tone={summary && summary.sessions.cancelledToday > 0 ? 'warn' : undefined}
              />
            </div>
            {/*
              Failures are named rather than folded into "cancelled". The two
              have different remedies — one is a person changing their mind and
              the other is a workflow to fix — and a single tile covering both
              would hide the one worth acting on.
            */}
            {summary && summary.sessions.failedToday > 0 && (
              <p className="hint">
                {summary.sessions.failedToday} failed today, counted separately from cancellations:
                nobody chose those.
              </p>
            )}
            <p className="hint">
              {summary
                ? `${summary.sessions.launchedInWindow} launched in the window · as of ${new Date(summary.computedAt).toLocaleTimeString('en-GB')}`
                : 'Counting…'}
            </p>
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Tokens</h2>
            <span className="chip">gateway ledger</span>
          </div>
          <div className="studio-card-body">
            <div className="tiles">
              <Tile
                label="Tokens today"
                value={today ? formatCompact(today.inputTokens + today.outputTokens) : '—'}
                note="input and output, since midnight UTC"
              />
              <Tile
                label="Tokens this week"
                value={
                  week
                    ? formatCompact(
                        week.reduce((total, day) => total + day.inputTokens + day.outputTokens, 0),
                      )
                    : '—'
                }
                note="the last seven days, whatever the chart shows"
              />
              <Tile
                label="Total tokens"
                value={allTime === null || allTime === undefined ? '—' : formatCompact(allTime)}
                note="every model call this deployment has made"
              />
            </div>
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Token usage over time</h2>
            <div className="toggle" role="group" aria-label="Chart range">
              {RANGES.map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={range === option}
                  onClick={() => setRange(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <div className="studio-card-body">
            {series === null ? (
              <p className="hint" style={{ marginTop: 0 }}>
                Loading the last {rangeDays(range)} days…
              </p>
            ) : (
              <TokenChart series={series} />
            )}
          </div>
        </section>

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Where the spend went</h2>
            {summary && <span className="chip">{summary.byWorkflow.length} workflows</span>}
          </div>
          <div className="studio-card-body">
            {summary && summary.byWorkflow.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Workflow</th>
                    <th>Sessions</th>
                    <th>Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byWorkflow.map((entry) => (
                    <tr key={entry.workflowId}>
                      <td>{entry.workflowName}</td>
                      <td>{entry.runs}</td>
                      <td>${(entry.spendCents / 100).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="hint" style={{ marginTop: 0 }}>
                Nothing launched in the window, so there is nothing to attribute. The total above is
                the sum of this table and will be zero too.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

/**
 * The chart, hand-rolled.
 *
 * There is no charting library here and adding one would be a poor trade: a
 * two-series line with five gridlines is about eighty lines of SVG, and the
 * smallest library that draws it is larger than the studio's whole bundle
 * budget for a screen most people open once a week.
 *
 * The geometry is computed in viewBox units and the element is sized in CSS, so
 * one drawing serves every width without a resize observer measuring the card.
 */
function TokenChart({ series }: { series: TokenDay[] }) {
  const peak = Math.max(...series.map((day) => Math.max(day.inputTokens, day.outputTokens)), 0);

  /*
   * A fresh deployment has nothing in the ledger, and the honest answer to that
   * is a sentence rather than a drawing. Axes over an all-zero series look
   * identical to axes over a failed load — the reader sees a flat line on a
   * baseline and cannot tell "nothing ran" from "something is broken" — so the
   * chart says which one it is in words and draws nothing.
   */
  if (peak === 0) {
    return (
      <p className="hint" style={{ marginTop: 0 }}>
        No tokens in the last {series.length} days. Nothing has called a model through the gateway in
        this range — a fresh deployment reads exactly like this, and the chart appears the first time
        a session runs a step.
      </p>
    );
  }

  const width = 760;
  const height = 240;
  const left = 56;
  const right = 16;
  const top = 16;
  const bottom = 30;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;

  /*
   * The axis tops out at a round number above the peak rather than at the peak
   * itself. A line that touches the top of its own frame reads as clipped, and
   * "1.2m" is a number somebody can hold in their head where "1,183,402" is
   * not.
   */
  const ceiling = niceCeiling(peak);
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  const x = (index: number) => left + (index / Math.max(series.length - 1, 1)) * plotWidth;
  const y = (value: number) => top + plotHeight - (value / ceiling) * plotHeight;
  const path = (pick: (day: TokenDay) => number) =>
    series.map((day, index) => `${index ? 'L' : 'M'}${x(index).toFixed(1)} ${y(pick(day)).toFixed(1)}`).join(' ');

  /*
   * Ninety labels along the bottom is a grey smear, so at most six are drawn —
   * anchored on the last day rather than the first, because "today" is the
   * label a reader looks for and an off-by-a-few-days final tick is the one
   * error that makes the whole axis suspect.
   */
  const every = Math.ceil(series.length / 6);

  return (
    <>
      <svg
        className="chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`Input and output tokens per day over the last ${series.length} days, peaking at ${formatCompact(peak)} tokens.`}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              className="chart-grid"
              x1={left}
              y1={y(tick * ceiling)}
              x2={width - right}
              y2={y(tick * ceiling)}
            />
            <text
              className="chart-axis-label"
              x={left - 8}
              y={y(tick * ceiling)}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {formatCompact(tick * ceiling)}
            </text>
          </g>
        ))}

        <path className="chart-input" d={path((day) => day.inputTokens)} />
        <path className="chart-output" d={path((day) => day.outputTokens)} />

        {series.map((day, index) =>
          (series.length - 1 - index) % every === 0 ? (
            <text
              key={day.day}
              className="chart-axis-label"
              x={x(index)}
              y={height - 10}
              textAnchor={index === 0 ? 'start' : index === series.length - 1 ? 'end' : 'middle'}
            >
              {formatDay(day.day)}
            </text>
          ) : null,
        )}
      </svg>

      <div className="chart-legend">
        <span>
          <svg className="chart-swatch" viewBox="0 0 16 8" aria-hidden="true">
            <line className="chart-input" x1="0" y1="4" x2="16" y2="4" />
          </svg>
          Input tokens
        </span>
        <span>
          <svg className="chart-swatch" viewBox="0 0 16 8" aria-hidden="true">
            <line className="chart-output" x1="0" y1="4" x2="16" y2="4" />
          </svg>
          Output tokens
        </span>
      </div>
      <p className="hint">
        Every day in the range is plotted, including the quiet ones. A series that skipped days with
        no traffic would draw a straight line across a weekend and make it look like steady usage.
      </p>
    </>
  );
}

function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone?: 'warn';
}) {
  return (
    <div className={`tile ${tone ?? ''}`}>
      <p className="tile-label">{label}</p>
      <p className="tile-value">{value}</p>
      <p className="tile-note">{note}</p>
    </div>
  );
}

function rangeDays(range: TokenWindow) {
  return Number.parseInt(range, 10);
}

/** The smallest round number at or above `value`, for an axis a reader can round off in their head. */
function niceCeiling(value: number) {
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 2, 2.5, 5, 10].find((factor) => value <= factor * magnitude) ?? 10;
  return step * magnitude;
}

/*
 * Token counts run to seven figures within a week of use, and a full-precision
 * number in a tile is read as a shape rather than as a quantity. One decimal
 * place rather than none, because "1m" covers a range wide enough that two very
 * different weeks would print the same tile.
 */
function formatCompact(value: number) {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}bn`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}m`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(Math.round(value));
}

/** `2026-08-30` as `30 Aug`, always UTC — the day the series bucketed it into. */
function formatDay(day: string) {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}
